import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, CheckSquare, Clock, Shield, Key, TrendingUp, Link2,
  Activity, Zap, ChevronRight,
} from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  AreaChart, Area,
} from 'recharts'
import api from '../services/api'

interface DashboardData {
  trifr: number | null
  ltifr: number | null
  daysSinceLastLTI: number | null
  openIncidents: number
  openCritical: number
  overdueActions: number
  activePermits: number
  pendingNotifications: number
  recentIncidents: any[]
  caCompletionRate: number | null
}

interface EnhancedData {
  actions_by_age: { days_0_30: number; days_31_60: number; days_60_plus: number }
  audit_by_site: { site: string; audit_count: number; avg_score: number | null }[]
  site_performance: { site: string; open_incidents: number; audit_score: number | null }[]
  incidents_by_type: { type_label: string; count: number }[]
  spark_incidents: { day: string; count: number }[]
}

const TEAL = '#00C4A1'
const NAVY = '#0D1B3E'
const DONUT_COLORS = [TEAL, '#2D7DD2', '#F5921E', '#6554C0', '#D93025', '#3DB54A']

const SEVERITY_COLOURS: Record<string, string> = {
  critical: 'badge-critical',
  high: 'badge-high',
  medium: 'badge-medium',
  low: 'badge-low',
}

function Sparkline({ data, color = TEAL }: { data: number[]; color?: string }) {
  if (!data || data.length < 2) return <div style={{ width: 72, height: 30 }} />
  const items = data.map(v => ({ v }))
  return (
    <ResponsiveContainer width={72} height={30}>
      <AreaChart data={items} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
        <Area
          type="monotone" dataKey="v"
          stroke={color} strokeWidth={1.5}
          fill={color} fillOpacity={0.12}
          dot={false} isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

function StatCard({
  label, value, sub, valueColor, icon: Icon, link, sparkData, sparkColor,
}: {
  label: string
  value: string | number | null
  sub?: string
  valueColor?: string
  icon: any
  link?: string
  sparkData?: number[]
  sparkColor?: string
}) {
  const content = (
    <div className="rounded-xl p-5 flex flex-col gap-2" style={{ background: '#fff', border: '1px solid var(--border)', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
      <div className="flex items-center justify-between">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: valueColor ? `${valueColor}18` : 'var(--accent-light)' }}
        >
          <Icon size={17} style={{ color: valueColor || TEAL }} />
        </div>
        {sparkData && sparkData.some(v => v > 0) && (
          <Sparkline data={sparkData} color={sparkColor || valueColor || TEAL} />
        )}
      </div>
      <div>
        <div className="text-2xl font-bold mt-1" style={{ color: valueColor || 'var(--text)' }}>
          {value === null ? '—' : value}
        </div>
        <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--text-muted)' }}>{label}</div>
        {sub && <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</div>}
      </div>
    </div>
  )
  return link ? <Link to={link} className="block hover:scale-[1.01] transition-transform">{content}</Link> : content
}

function SectionCard({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-xl p-5" style={{ background: '#fff', border: '1px solid var(--border)', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{title}</h2>
        {action}
      </div>
      {children}
    </div>
  )
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg px-3 py-2 text-xs shadow-lg" style={{ background: NAVY, color: '#fff', border: 'none' }}>
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-1.5">
          <span style={{ color: p.color }}>●</span>
          <span style={{ color: 'rgba(255,255,255,0.7)' }}>{p.name}:</span>
          <span className="font-medium">{p.value ?? '—'}</span>
        </div>
      ))}
    </div>
  )
}

function deriveInsights(data: DashboardData, enhanced: EnhancedData | null): string[] {
  const insights: string[] = []
  if (!data) return insights

  if (data.trifr !== null && data.trifr > 5) {
    insights.push(`TRIFR of ${data.trifr.toFixed(2)} exceeds the 5.0 industry benchmark — review recent injury classifications and hours worked data.`)
  } else if (data.trifr !== null) {
    insights.push(`TRIFR of ${data.trifr.toFixed(2)} is within safe range. Continue monitoring lead indicators to sustain performance.`)
  } else {
    insights.push('No hours worked data recorded yet — log hours in Metrics to enable TRIFR/LTIFR calculations.')
  }

  if (data.openCritical > 0) {
    insights.push(`${data.openCritical} critical incident${data.openCritical > 1 ? 's remain' : ' remains'} open. Immediate investigation and ICAM required under WHS Act 2011.`)
  }

  if (data.overdueActions > 5) {
    insights.push(`${data.overdueActions} overdue corrective actions signal a systemic close-out barrier — consider assigning a dedicated action owner or escalating to leadership.`)
  } else if (data.overdueActions > 0) {
    insights.push(`${data.overdueActions} corrective action${data.overdueActions > 1 ? 's are' : ' is'} overdue. Review action owners and due dates.`)
  }

  if (enhanced) {
    const worst = enhanced.site_performance.find(s => s.open_incidents > 0)
    if (worst) {
      insights.push(`${worst.site} has the highest open incident count (${worst.open_incidents}). Prioritise site visit and audit cadence.`)
    }
    const { days_0_30, days_31_60, days_60_plus } = enhanced.actions_by_age
    if (days_60_plus > 0) {
      insights.push(`${days_60_plus} corrective action${days_60_plus > 1 ? 's' : ''} over 60 days old — aged actions increase legal exposure under Safe Work Australia guidelines.`)
    }
    if (!insights.length) {
      insights.push('All safety metrics within target. Maintain current audit frequency and near-miss reporting culture to sustain performance.')
    }
  }

  return insights.slice(0, 3)
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [enhanced, setEnhanced] = useState<EnhancedData | null>(null)
  const [trend, setTrend] = useState<any[]>([])
  const [linkedActivity, setLinkedActivity] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      api.get('/metrics/dashboard-summary'),
      api.get('/metrics/dashboard-enhanced').catch(() => ({ data: null })),
      api.get('/metrics/monthly-trend?months=12').catch(() => ({ data: [] })),
      api.get('/relationships/activity?limit=5').catch(() => ({ data: { data: [] } })),
    ]).then(([summaryRes, enhRes, trendRes, activityRes]) => {
      setData(summaryRes.data)
      setEnhanced(enhRes.data)
      setTrend(trendRes.data || [])
      setLinkedActivity(activityRes.data?.data || [])
    }).catch(console.error).finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading dashboard...</div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm" style={{ color: 'var(--red)' }}>Failed to load dashboard. Please refresh.</div>
      </div>
    )
  }

  const d = data
  const sparkNumbers = (enhanced?.spark_incidents || []).map(s => s.count)
  const trendChartData = trend.map(t => ({
    label: t.start ? new Date(t.start).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' }) : String(t.month),
    trifr: t.trifr,
    incidents: t.total_incidents,
  }))

  const insights = deriveInsights(d, enhanced)
  const totalActionAge = (enhanced?.actions_by_age.days_0_30 || 0)
    + (enhanced?.actions_by_age.days_31_60 || 0)
    + (enhanced?.actions_by_age.days_60_plus || 0)

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text)' }}>Dashboard</h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <Link to="/incidents/new" className="btn btn-primary text-sm gap-1.5" style={{ background: 'var(--accent)', color: '#fff' }}>
          <AlertTriangle size={15} />
          Report Incident
        </Link>
      </div>

      {/* Regulatory notification alert */}
      {d.pendingNotifications > 0 && (
        <div className="p-4 rounded-xl flex items-center gap-3" style={{ background: 'var(--red-light)', border: '1px solid var(--red)' }}>
          <Clock size={18} style={{ color: 'var(--red)', flexShrink: 0 }} />
          <div className="flex-1">
            <p className="font-semibold text-sm" style={{ color: 'var(--red)' }}>
              {d.pendingNotifications} regulatory notification{d.pendingNotifications > 1 ? 's' : ''} pending acknowledgement
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--red)' }}>
              Review incidents for SafeWork notification deadlines
            </p>
          </div>
          <Link to="/incidents" className="text-xs font-semibold flex items-center gap-1" style={{ color: 'var(--red)' }}>
            View <ChevronRight size={13} />
          </Link>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          label="TRIFR (YTD)"
          value={d.trifr !== null ? d.trifr.toFixed(2) : null}
          sub="per million hours"
          icon={TrendingUp}
          valueColor={d.trifr !== null && d.trifr > 5 ? 'var(--red)' : TEAL}
          sparkData={sparkNumbers}
        />
        <StatCard
          label="Days LTI-Free"
          value={d.daysSinceLastLTI ?? '—'}
          sub={d.daysSinceLastLTI === null ? 'No LTIs recorded' : 'consecutive days'}
          icon={Shield}
          valueColor={d.daysSinceLastLTI !== null && d.daysSinceLastLTI > 30 ? TEAL : 'var(--amber)'}
          sparkData={sparkNumbers}
          sparkColor={TEAL}
        />
        <StatCard
          label="Open Critical"
          value={d.openCritical}
          sub="incidents"
          icon={AlertTriangle}
          valueColor={d.openCritical > 0 ? 'var(--red)' : TEAL}
          link="/incidents?severity=critical"
          sparkData={sparkNumbers}
          sparkColor={d.openCritical > 0 ? 'var(--red)' : TEAL}
        />
        <StatCard
          label="Overdue Actions"
          value={d.overdueActions}
          sub="corrective actions"
          icon={CheckSquare}
          valueColor={d.overdueActions > 0 ? 'var(--amber)' : TEAL}
          link="/corrective-actions?status=overdue"
          sparkData={sparkNumbers}
          sparkColor={d.overdueActions > 0 ? 'var(--amber)' : TEAL}
        />
        <StatCard
          label="Active Permits"
          value={d.activePermits}
          sub="permits to work"
          icon={Key}
          link="/permits?status=active"
          sparkData={sparkNumbers}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* TRIFR trend */}
        <div className="md:col-span-3 rounded-xl p-5" style={{ background: '#fff', border: '1px solid var(--border)', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>TRIFR Trend — 12 Months</h2>
            <Link to="/metrics" className="text-xs font-medium flex items-center gap-1" style={{ color: TEAL }}>
              Full report <ChevronRight size={12} />
            </Link>
          </div>
          {trendChartData.length === 0 || trendChartData.every(t => t.trifr === null && t.incidents === 0) ? (
            <div className="flex items-center justify-center h-40 text-xs" style={{ color: 'var(--text-muted)' }}>
              No trend data — log hours worked to enable rate calculations
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={trendChartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone" dataKey="trifr" name="TRIFR"
                  stroke={TEAL} strokeWidth={2.5} dot={{ r: 3, fill: TEAL, strokeWidth: 0 }}
                  connectNulls activeDot={{ r: 5 }}
                />
                <Line
                  type="monotone" dataKey="incidents" name="Incidents"
                  stroke="#2D7DD2" strokeWidth={1.5} dot={false}
                  connectNulls strokeDasharray="4 3"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Incidents by type donut */}
        <div className="md:col-span-2 rounded-xl p-5" style={{ background: '#fff', border: '1px solid var(--border)', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <h2 className="text-sm font-semibold mb-4" style={{ color: 'var(--text)' }}>Incidents by Type (YTD)</h2>
          {!enhanced || enhanced.incidents_by_type.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-xs" style={{ color: 'var(--text-muted)' }}>
              No incidents recorded this year
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={enhanced.incidents_by_type}
                  dataKey="count"
                  nameKey="type_label"
                  cx="50%" cy="50%"
                  innerRadius={48} outerRadius={72}
                  paddingAngle={2}
                >
                  {enhanced.incidents_by_type.map((_, i) => (
                    <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: any, n: any) => [v, n]} contentStyle={{ fontSize: 11 }} />
                <Legend
                  iconSize={8}
                  formatter={(v) => <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{v}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Analytics row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Open actions by age */}
        <SectionCard title="Open Actions by Age">
          {!enhanced ? (
            <div className="text-xs text-center py-6" style={{ color: 'var(--text-muted)' }}>Loading…</div>
          ) : totalActionAge === 0 ? (
            <div className="text-xs text-center py-6" style={{ color: 'var(--text-muted)' }}>No open actions</div>
          ) : (
            <div className="space-y-3">
              {[
                { label: '0–30 days', count: enhanced.actions_by_age.days_0_30, color: TEAL },
                { label: '31–60 days', count: enhanced.actions_by_age.days_31_60, color: 'var(--amber)' },
                { label: '60+ days', count: enhanced.actions_by_age.days_60_plus, color: 'var(--red)' },
              ].map(({ label, count, color }) => (
                <div key={label}>
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                    <span className="font-semibold" style={{ color }}>{count}</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg)' }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: totalActionAge > 0 ? `${(count / totalActionAge) * 100}%` : '0%',
                        background: color,
                      }}
                    />
                  </div>
                </div>
              ))}
              <Link
                to="/corrective-actions"
                className="flex items-center gap-1 text-xs font-medium mt-2"
                style={{ color: TEAL }}
              >
                View all actions <ChevronRight size={12} />
              </Link>
            </div>
          )}
        </SectionCard>

        {/* Audit compliance by site */}
        <SectionCard
          title="Audit Compliance by Site"
          action={
            <Link to="/audits" className="text-xs font-medium flex items-center gap-0.5" style={{ color: TEAL }}>
              All <ChevronRight size={12} />
            </Link>
          }
        >
          {!enhanced || enhanced.audit_by_site.length === 0 ? (
            <div className="text-xs text-center py-6" style={{ color: 'var(--text-muted)' }}>No audit data for last 90 days</div>
          ) : (
            <div className="space-y-3">
              {enhanced.audit_by_site.slice(0, 5).map(site => {
                const score = site.avg_score ?? 0
                const color = score >= 80 ? TEAL : score >= 60 ? 'var(--amber)' : 'var(--red)'
                return (
                  <div key={site.site}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="truncate max-w-[120px]" style={{ color: 'var(--text-secondary)' }}>{site.site}</span>
                      <span className="font-semibold ml-2" style={{ color }}>
                        {site.avg_score !== null ? `${site.avg_score}%` : 'N/A'}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg)' }}>
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${score}%`, background: color, transition: 'width 0.5s ease' }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </SectionCard>

        {/* Safety Intelligence */}
        <div
          className="rounded-xl p-5"
          style={{ background: 'linear-gradient(135deg, #0D1B3E 0%, #1A2D5A 100%)', border: '1px solid rgba(0,196,161,0.2)' }}
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,196,161,0.2)' }}>
              <Zap size={14} style={{ color: TEAL }} />
            </div>
            <h2 className="text-sm font-semibold text-white">Safety Intelligence</h2>
          </div>
          <div className="space-y-3">
            {insights.length === 0 ? (
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Gathering insights from your safety data…</p>
            ) : insights.map((insight, i) => (
              <div key={i} className="flex gap-2.5">
                <div
                  className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-1.5"
                  style={{ background: TEAL }}
                />
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>{insight}</p>
              </div>
            ))}
          </div>
          <Link
            to="/metrics"
            className="mt-4 flex items-center gap-1 text-xs font-medium"
            style={{ color: TEAL }}
          >
            <Activity size={12} />
            Full analytics
          </Link>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Site performance */}
        <SectionCard
          title="Site Performance Overview"
          action={
            <Link to="/metrics" className="text-xs font-medium flex items-center gap-0.5" style={{ color: TEAL }}>
              Details <ChevronRight size={12} />
            </Link>
          }
        >
          {!enhanced || enhanced.site_performance.length === 0 ? (
            <div className="text-xs text-center py-4" style={{ color: 'var(--text-muted)' }}>No site data available</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th className="text-left pb-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Site</th>
                  <th className="text-center pb-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Open Incidents</th>
                  <th className="text-right pb-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Audit Score</th>
                </tr>
              </thead>
              <tbody>
                {enhanced.site_performance.map((s, i) => (
                  <tr key={s.site} style={{ borderBottom: i < enhanced.site_performance.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <td className="py-2 font-medium truncate max-w-[120px]" style={{ color: 'var(--text)' }}>{s.site}</td>
                    <td className="py-2 text-center">
                      <span
                        className="inline-flex items-center justify-center rounded-full text-xs font-semibold"
                        style={{
                          width: 22, height: 22,
                          background: s.open_incidents > 0 ? 'var(--red-light)' : 'var(--accent-light)',
                          color: s.open_incidents > 0 ? 'var(--red)' : 'var(--accent)',
                        }}
                      >
                        {s.open_incidents}
                      </span>
                    </td>
                    <td className="py-2 text-right font-medium" style={{ color: s.audit_score !== null ? (s.audit_score >= 80 ? TEAL : 'var(--amber)') : 'var(--text-muted)' }}>
                      {s.audit_score !== null ? `${s.audit_score}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>

        {/* Recent incidents */}
        <SectionCard
          title="Recent Incidents"
          action={
            <Link to="/incidents" className="text-xs font-medium flex items-center gap-0.5" style={{ color: TEAL }}>
              View all <ChevronRight size={12} />
            </Link>
          }
        >
          {d.recentIncidents.length === 0 ? (
            <p className="text-xs text-center py-6" style={{ color: 'var(--text-muted)' }}>
              No incidents recorded yet — great work!
            </p>
          ) : (
            <div className="space-y-1">
              {d.recentIncidents.slice(0, 6).map((inc: any) => (
                <Link
                  key={inc.id}
                  to={`/incidents/${inc.id}`}
                  className="flex items-center gap-3 p-2 rounded-lg transition-all"
                  style={{ '--hover-bg': 'var(--bg)' } as any}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <span className={`badge ${SEVERITY_COLOURS[inc.ai_severity] || 'badge-medium'} text-xs`}>
                    {inc.ai_severity || '?'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{inc.description}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      {inc.incident_number} · {new Date(inc.date_occurred).toLocaleDateString('en-AU')}
                    </p>
                  </div>
                  <span
                    className="badge text-xs flex-shrink-0"
                    style={{
                      background: inc.status === 'open' ? 'var(--amber-light)' : 'var(--green-light)',
                      color: inc.status === 'open' ? 'var(--amber)' : 'var(--green)',
                    }}
                  >
                    {inc.status}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Cross-tenant activity */}
      {linkedActivity.length > 0 && (
        <SectionCard
          title="Cross-Tenant Activity"
          action={
            <Link to="/settings/relationships" className="text-xs font-medium flex items-center gap-0.5" style={{ color: TEAL }}>
              Manage <ChevronRight size={12} />
            </Link>
          }
        >
          <div className="space-y-1.5">
            {linkedActivity.map((rec: any) => {
              const meta = (() => { try { return JSON.parse(rec.metadata || '{}') } catch { return {} } })()
              return (
                <div key={rec.id} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: 'var(--bg)' }}>
                  <Link2 size={13} style={{ color: TEAL, flexShrink: 0 }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>
                      [{rec.partner_name}] {rec.source_record_type?.replace('_', ' ')}
                      {meta.clean_record?.incident_number ? ` · ${meta.clean_record.incident_number}` : ''}
                    </p>
                    <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                      {meta.clean_record?.description?.slice(0, 80) || rec.source_record_type}
                    </p>
                  </div>
                  <p className="text-xs shrink-0" style={{ color: 'var(--text-muted)' }}>
                    {new Date(rec.shared_at).toLocaleDateString('en-AU')}
                  </p>
                </div>
              )
            })}
          </div>
        </SectionCard>
      )}
    </div>
  )
}
